import { z } from 'zod';
import { hopDongThueMySchema, thucNhanSchema } from './thucNhan.js';
/**
 * DTO của tab "Hiệu suất" (trang chi tiết kênh).
 *
 * Chỉ gồm những gì YouTube API thật trả về. Không có: khán giả hằng tháng,
 * người xem mới/thường xuyên, người xem riêng biệt, số liệu theo giờ, bài đăng
 * cộng đồng, kênh khán giả cũng xem, thời điểm khán giả xem — API không mở.
 */
/** Nguồn của một con số, để giao diện chấm màu đúng. */
export const NGUON_SO_LIEU = ['analytics', 'reporting', 'app'];
export const nguonSoLieuSchema = z.enum(NGUON_SO_LIEU);
export const KET_QUA_BINH_THUONG = ['cao', 'thap', 'binh-thuong'];
/** Khoảng "thường lệ" do app tự tính từ lịch sử kênh. */
export const khoangBinhThuongSchema = z.object({
    thap: z.number(),
    cao: z.number(),
    giaTri: z.number(),
    ketQua: z.enum(KET_QUA_BINH_THUONG),
    soCuaSo: z.number().int().nonnegative(),
});
/** Một thẻ chỉ số trên hàng thẻ. */
export const theChiSoSchema = z.object({
    khoa: z.string(),
    nhan: z.string(),
    /** `null` = **không có dữ liệu** cho kỳ này; giao diện hiện "—", không hiện 0. */
    giaTri: z.number().nullable(),
    /** `so`, `gio`, `tien`, `phan-tram`, `thoi-luong` — quyết định cách định dạng. */
    dinhDang: z.enum(['so', 'gio', 'tien', 'phan-tram', 'thoi-luong']),
    nguon: nguonSoLieuSchema,
    /** `true` khi YouTube ghi rõ là số ước tính (biểu tượng đồng hồ). */
    laUocTinh: z.boolean(),
    /** Phần trăm so kỳ trước; `null` khi kỳ trước thiếu ngày hoặc kỳ là "toàn thời gian". */
    deltaPercent: z.number().nullable(),
    binhThuong: khoangBinhThuongSchema.nullable(),
    /**
     * Ghi chú thay cho dòng so sánh, dùng khi con số **không phủ hết kỳ** — ví dụ
     * lượt hiển thị chỉ có từ ngày app tạo reporting job.
     */
    ghiChu: z.string().nullable(),
    /** Chuỗi theo ngày để vẽ biểu đồ. */
    diem: z.array(z.object({ date: z.string(), giaTri: z.number(), daChot: z.boolean() })),
});
/** Một dòng nội dung (video) trong bảng xếp hạng. */
export const dongNoiDungSchema = z.object({
    youtubeVideoId: z.string(),
    title: z.string(),
    thumbnailUrl: z.string().nullable(),
    publishedAt: z.iso.datetime().nullable(),
    views: z.number().int().nonnegative(),
    /** Thời lượng xem trung bình (giây) và phần trăm thời lượng video. */
    averageViewDuration: z.number().int().nonnegative(),
    averageViewPercentage: z.number().nullable(),
});
/** Ngày có video xuất bản, để vẽ dải ▶ trên biểu đồ. */
export const mocXuatBanSchema = z.object({
    date: z.string(),
    title: z.string(),
});
export const hieuSuatTongQuanSchema = z.object({
    from: z.string(),
    to: z.string(),
    /** Khoảng của **kỳ trước**, để thẻ chỉ số nói rõ mình so với ngày nào. */
    prevFrom: z.string(),
    prevTo: z.string(),
    /** Ngày gần nhất **đã chốt** số liệu; ngày sau đó vẽ cột xám. */
    ngayDaChot: z.string(),
    the: z.array(theChiSoSchema),
    mocXuatBan: z.array(mocXuatBanSchema),
    /** Lượt xem từ "Các tính năng duyệt xem" + "Video đề xuất" so với thường lệ. */
    viSao: z
        .object({
        nhan: z.string(),
        binhThuong: khoangBinhThuongSchema,
    })
        .nullable(),
    noiDungHangDau: z.array(dongNoiDungSchema),
});
export const nguonLuuLuongDongSchema = z.object({
    sourceType: z.string(),
    nhan: z.string(),
    views: z.number().int().nonnegative(),
    phanTram: z.number(),
});
/** Một video trong khối "khoảnh khắc giữ chân người xem". */
export const giuChanVideoSchema = z.object({
    youtubeVideoId: z.string(),
    title: z.string(),
    thumbnailUrl: z.string().nullable(),
    /** Tỷ lệ còn xem ở giây 30 (0–100). */
    phanTramGiay30: z.number(),
});
export const hieuSuatNoiDungSchema = z.object({
    from: z.string(),
    to: z.string(),
    /** Khoảng của **kỳ trước**, để thẻ chỉ số nói rõ mình so với ngày nào. */
    prevFrom: z.string(),
    prevTo: z.string(),
    ngayDaChot: z.string(),
    /** Loại nội dung kênh có trong kỳ: `videoOnDemand`, `shorts`, `liveStream`… */
    loaiNoiDungCo: z.array(z.string()),
    the: z.array(theChiSoSchema),
    mocXuatBan: z.array(mocXuatBanSchema),
    nguonLuuLuong: z.array(nguonLuuLuongDongSchema),
    videoHangDau: z.array(dongNoiDungSchema),
    giuChan: z.object({
        caoHon: z.array(giuChanVideoSchema),
        thapHon: z.array(giuChanVideoSchema),
        /** Trung vị của kênh, để giao diện ghi mốc so sánh. */
        trungVi: z.number().nullable(),
    }),
    /** Phễu lượt hiển thị; `null` khi kỳ nằm trước ngày tạo reporting job. */
    pheu: z
        .object({
        /** Ngày đầu tiên trong kỳ **có** số của Reporting API. */
        tuNgay: z.string(),
        /** `false` khi Reporting API chưa phủ hết kỳ (job tạo sau ngày bắt đầu). */
        duPhu: z.boolean(),
        impressions: z.number(),
        ctr: z.number(),
        viewsTuHienThi: z.number(),
        averageViewDuration: z.number(),
        gioXemTuHienThi: z.number(),
    })
        .nullable(),
});
export const hieuSuatDoiTuongSchema = z.object({
    from: z.string(),
    to: z.string(),
    /** Khoảng của **kỳ trước**, để thẻ chỉ số nói rõ mình so với ngày nào. */
    prevFrom: z.string(),
    prevTo: z.string(),
    ngayDaChot: z.string(),
    the: z.array(theChiSoSchema),
    mocXuatBan: z.array(mocXuatBanSchema),
    doTuoi: z.array(z.object({
        ageGroup: z.string(),
        nam: z.number(),
        nu: z.number(),
        tuXacDinh: z.number(),
    })),
    /** `true` khi số liệu độ tuổi lấy từ bản đã lưu (không gọi API lần này). */
    doTuoiTuCache: z.boolean(),
    quocGia: z.array(z.object({ country: z.string(), nhan: z.string(), views: z.number(), phanTram: z.number() })),
    /** `true` khi số liệu quốc gia lấy từ bản đã lưu (không gọi API lần này). */
    quocGiaTuCache: z.boolean(),
    /** Phần trăm theo **thời gian xem** như Studio, không theo lượt xem. */
    thietBi: z.array(z.object({
        deviceType: z.string(),
        nhan: z.string(),
        views: z.number(),
        gioXem: z.number(),
        phanTram: z.number(),
    })),
    theoDangKy: z.array(z.object({
        subscribedStatus: z.string(),
        nhan: z.string(),
        gioXem: z.number(),
        phanTram: z.number(),
    })),
});
export const hieuSuatDoanhThuSchema = z.object({
    from: z.string(),
    to: z.string(),
    /** Khoảng của **kỳ trước**, để thẻ chỉ số nói rõ mình so với ngày nào. */
    prevFrom: z.string(),
    prevTo: z.string(),
    ngayDaChot: z.string(),
    the: z.array(theChiSoSchema),
    mocXuatBan: z.array(mocXuatBanSchema),
    /** Sáu tháng gần nhất; tháng hiện tại `dangDienRa = true`. */
    theoThang: z.array(z.object({ period: z.string(), doanhThu: z.number(), dangDienRa: z.boolean() })),
    hieuSuatNoiDung: z.array(z.object({
        youtubeVideoId: z.string(),
        title: z.string(),
        thumbnailUrl: z.string().nullable(),
        doanhThu: z.number(),
        engagedViews: z.number(),
        rpm: z.number().nullable(),
    })),
    kiemTien: z.object({
        quangCaoTrangXem: z.number(),
        premium: z.number(),
        /** CPM dựa trên lượt phát (`playbackBasedCpm`). */
        cpmLuotPhat: z.number().nullable(),
        cpm: z.number().nullable(),
    }),
    /**
     * Chia theo **loại nội dung** (`creatorContentType`), để đối chiếu với bộ lọc
     * "Video / Shorts / Phát trực tiếp" của YouTube Studio.
     *
     * Lý do có khối này: Studio mở sẵn bộ lọc **chỉ Video**, còn thẻ của app cộng
     * **mọi loại nội dung**. Kênh Pain kỳ 11/09–08/10/2026 lệch đúng vì vậy:
     * Studio 752,9 N lượt xem có chủ đích / 1.096,88 US$, app 848.513 / 1.192,02
     * US$ — 95.613 lượt và 95,14 US$ kia là Shorts và phát trực tiếp.
     *
     * `null` ở `engagedViews` hoặc `doanhThu` nghĩa là **chưa lấy được**, không
     * phải 0: hàng ghi trước lượt lấy có hai cột đó, hoặc kênh không được xem
     * tiền.
     */
    theoLoaiNoiDung: z.array(z.object({
        /** `videoOnDemand`, `shorts`, `liveStream`… */
        contentType: z.string(),
        nhan: z.string(),
        views: z.number().int().nonnegative(),
        gioXem: z.number(),
        engagedViews: z.number().nullable(),
        doanhThu: z.number().nullable(),
        /** Phần trăm lượt xem của dòng này trên tổng lượt xem các loại. */
        phanTram: z.number(),
    })),
    /** Đã gộp các mã cùng nhãn Studio; `phanTram` tính trên tổng doanh thu quảng cáo. */
    theoLoaiQuangCao: z.array(z.object({
        nhan: z.string(),
        /** Các mã `adType` gộp vào dòng này, để tra khi cần. */
        maGoc: z.array(z.string()),
        doanhThu: z.number(),
        phanTram: z.number(),
    })),
    /**
     * "Doanh thu thực nhận": trừ thuế Mỹ, phần Network rồi VAT.
     *
     * Thay khối "Sau khi chia với Network" cũ — khối đó chỉ trừ một khoản nên
     * không phải số công ty thật nhận.
     */
    thucNhan: thucNhanSchema.extend({
        /** `false` = chưa ai đặt tỷ lệ, đang dùng mặc định + hợp đồng Network. */
        daDat: z.boolean(),
        /** `true` khi không lấy được doanh thu theo quốc gia (thuế Mỹ tính 0). */
        thieuSoMy: z.boolean(),
        /** `true` khi kỳ có ngày không thuộc Network nào (ngày vào Network sai?). */
        thieuNetwork: z.boolean(),
        soNgayThieuNetwork: z.number().int().nonnegative(),
        /** Hợp đồng cấp thuế Mỹ cho kỳ; `null` khi chưa khai ở đâu cả. */
        hopDongThueMy: hopDongThueMySchema.nullable(),
        /** `true` khi kỳ có ngày chưa khai thuế Mỹ — đang tính mặc định 30%. */
        thieuThueMy: z.boolean(),
        soNgayThieuThueMy: z.number().int().nonnegative(),
        /** `true` khi tỷ lệ Network là bình quân của nhiều đoạn trong kỳ. */
        tyLeBinhQuan: z.boolean(),
    }),
});
/* ------------------------------------------------------------------ */
/* Nhãn tiếng Việt theo đúng chữ của YouTube Studio                    */
/* ------------------------------------------------------------------ */
/*
 * Nhãn nguồn lưu lượng (`NHAN_NGUON_LUU_LUONG`, `nhanNguonLuuLuong`) và
 * `NGUON_DUYET_XEM` nay ở `nguonLuuLuong.ts` — một bảng duy nhất cho cả app.
 */
export const NHAN_THIET_BI = {
    MOBILE: 'Điện thoại',
    DESKTOP: 'Máy tính',
    TABLET: 'Máy tính bảng',
    TV: 'TV',
    GAME_CONSOLE: 'Máy chơi game',
    UNKNOWN_PLATFORM: 'Không xác định',
};
/**
 * `adType` → nhãn YouTube Studio tiếng Việt.
 *
 * Studio gộp nhiều mã vào cùng một dòng (ví dụ `auctionTrueviewInstream`,
 * `auctionInstream` và `auctionInstreamSkippable` đều là quảng cáo trong luồng
 * có thể bỏ qua bán theo đấu giá), nên chỗ hiển thị **gộp theo nhãn**.
 *
 * Danh sách mã lấy từ dữ liệu thật của 7 kênh live cộng với bảng giá trị của
 * Google; mã lạ thì giữ nguyên mã và ghi cảnh báo thay vì vỡ giao diện.
 */
export const NHAN_LOAI_QUANG_CAO = {
    auctionInstream: 'Có thể bỏ qua (Đấu giá)',
    auctionInstreamSkippable: 'Có thể bỏ qua (Đấu giá)',
    auctionTrueviewInstream: 'Có thể bỏ qua (Đấu giá)',
    auctionInstreamUnskippable: 'Không thể bỏ qua (Đấu giá)',
    auctionBumperInstream: 'Quảng cáo đệm (Đấu giá)',
    auctionDisplay: 'Quảng cáo hiển thị hình ảnh (Đấu giá)',
    auctionTrueviewInslate: 'TrueView in-slate (Đấu giá)',
    auctionUnknown: 'Đấu giá (không xác định)',
    reservedInstream: 'Có thể bỏ qua (Đặt trước)',
    reservedInstreamSkippable: 'Có thể bỏ qua (Đặt trước)',
    reservedInstreamSelect: 'YouTube Select (Đặt trước)',
    reservedInstreamUnskippable: 'Không thể bỏ qua (Đặt trước)',
    reservedBumperInstream: 'Quảng cáo đệm (Đặt trước)',
    reservedDisplay: 'Quảng cáo hiển thị hình ảnh (Đặt trước)',
    reservedClickToPlay: 'Nhấp để phát (Đặt trước)',
    reservedMasthead: 'Masthead (Đặt trước)',
    reservedUnknown: 'Đặt trước (không xác định)',
    unknown: 'Không xác định',
};
/**
 * Nhãn của `creatorContentType`.
 *
 * ⚠️ YouTube trả **cả hai dạng** số ít và số nhiều tuỳ loại — kênh thật trả
 * `posts` (số nhiều) nên trước 27/10/2026 màn Hiệu suất in nguyên khoá thô
 * "posts" ra giao diện (chỗ đọc nhãn dùng `?? khoá`, hỏng im lặng). Vì không
 * biết trước Google còn trả dạng nào, giữ **cả hai** biến thể cho mọi giá trị
 * thay vì đoán, và `nhanLoaiNoiDung` hạ về số ít khi tra không thấy.
 */
export const NHAN_LOAI_NOI_DUNG = {
    videoOnDemand: 'Video',
    videoOnDemands: 'Video',
    shorts: 'Shorts',
    short: 'Shorts',
    liveStream: 'Sự kiện trực tiếp',
    liveStreams: 'Sự kiện trực tiếp',
    story: 'Story',
    stories: 'Story',
    post: 'Bài đăng',
    posts: 'Bài đăng',
};
/**
 * Nhãn tiếng Việt của một `creatorContentType`.
 *
 * Tra `NHAN_LOAI_NOI_DUNG` trước; không thấy thì thử hạ số nhiều (`posts` →
 * `post`, `stories` → `story`) rồi mới trả lại khoá thô. Nhờ vậy một giá trị
 * mới của YouTube chỉ lọt ra giao diện khi nó thật sự là từ app chưa biết.
 */
export function nhanLoaiNoiDung(loai) {
    const khop = NHAN_LOAI_NOI_DUNG[loai];
    if (khop !== undefined)
        return khop;
    const soIt = loai.endsWith('ies')
        ? `${loai.slice(0, -3)}y`
        : loai.endsWith('s')
            ? loai.slice(0, -1)
            : loai;
    return NHAN_LOAI_NOI_DUNG[soIt] ?? loai;
}
export const NHAN_DANG_KY = {
    SUBSCRIBED: 'Người đăng ký',
    UNSUBSCRIBED: 'Người không đăng ký',
};
export const NHAN_NHOM_TUOI = {
    'age13-17': '13–17',
    'age18-24': '18–24',
    'age25-34': '25–34',
    'age35-44': '35–44',
    'age45-54': '45–54',
    'age55-64': '55–64',
    'age65-': 'Trên 65',
};
//# sourceMappingURL=hieuSuat.js.map